import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import {
  FormattedMessage,
  FormattedDate,
  injectIntl,
  intlShape,
  FormattedTime,
  FormattedNumber,
} from "react-intl";
import { Paragraph, Heading2, Heading3, Icon, Hidden } from "../../components";
import { currencyFormat, truncate } from "../../utils/formatters";
import { EVENT_TYPES } from "../../utils/courses";
import { DEFAULT_TIMEZONE } from "../../utils/calc";
import { lighten } from "polished";

const Wrapper = styled.header`
  margin-bottom: 2rem;
  margin-right: 2rem;
`;

const HeaderWrapper = styled.header`
  display: flex;
`;

const Heading = styled(Heading2)`
  font-size: 1.25rem;
  margin: 0;

  small {
    font-size: ${props => props.theme.fontSizes.small};
  }
  @media (${props => props.theme.tabletScreen}) {
    font-size: 1.75rem;

    small {
      font-size: 80%;
    }
  }
`;

const AuthStatus = styled.span`
  margin-left: 0.5rem;
  vertical-align: top;
`;

const PriceOption = styled.span`
  color: ${props => props.theme.info};
  span {
    margin-left: 0.5rem;
  }
`;

const EventAdditionalInfo = styled.span`
  display: block;
  color: ${props => props.theme.info};
  font-family: ${props => props.theme.fontFamilyBase};
`;

const TitleWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (${props => props.theme.desktopScreen}) {
    flex-direction: row;
    align-items: center;
  }
`;

const HeaderImage = styled.figure`
  display: block;
  margin: 0 0.3rem 0 0;
  width: 3rem;
  min-width: 3rem;
  height: 3rem;
  min-height: 3rem;
  background-size: cover;
  background-position: center;
  border-radius: ${props => props.theme.borderRadius};

  @media (${props => props.theme.tabletScreen}) {
    margin: 0 1rem 0 0;
    width: 4rem;
    min-width: 4rem;
    height: 4rem;
    min-height: 4rem;
  }
`;

const CourseTitle = styled(Heading3)`
  font-size: 1rem;
  background-color: ${props => props.theme.light};
  padding: 0.125rem;
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.dark};
  font-weight: normal;

  @media (${props => props.theme.tabletScreen}) {
    font-size: 1.5rem;
    margin-bottom: 0;
    padding: 0.5rem;
  }
`;

const Description = styled.p`
  font-size: ${props => props.theme.fontSizes.small};
  padding: 0.25rem;
`;

const PriceOptionWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.125rem;
  color: ${props => props.theme.info};
  background-color: ${props => lighten(0.4, props.theme.info)};
  border-radius: ${props => props.theme.borderRadius};
  font-size: ${props => props.theme.fontSizes.small};

  @media (${props => props.theme.tabletScreen}) {
    font-size: ${props => props.theme.fontSizes.default};
    padding: 0.5rem;
    font-weight: bold;
    margin-top: 0.25rem;
  }
`;

const AppointmentInfo = styled.section`
  margin-top: 0.25rem;
  padding: 0.25rem;
  background-color: ${props => props.theme.light};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.dark};
  display: inline-block;

  ${Paragraph} {
    margin-bottom: 0;

    font-size: ${props => props.theme.fontSizes.tiny};
    @media (${props => props.theme.tabletScreen}) {
      font-size: ${props => props.theme.fontSizes.small};
    }
  }

  ${Description} {
    display: none;
    @media (${props => props.theme.tabletScreen}) {
      display: block;
    }
  }

  i {
    display: none;
    @media (${props => props.theme.tabletScreen}) {
      display: inline-block;
    }
  }
`;

const Title = ({ course, event, intl, unlocked, priceOption, image }) => {
  const getAppointmentsInfo = () => {
    switch (event.typeOf) {
      case EVENT_TYPES.block: {
        return (
          <Paragraph mono>
            <Icon name="calendar" color="dark" spaced="right" size="small" />
            <FormattedDate month="long" day="2-digit" value={Date.parse(event.startDate)} />
            {event.appointments && event.appointments.length > 1 ? (
              <span>
                {" - "}
                <FormattedDate month="long" day="2-digit" value={Date.parse(event.endDate)} />
                {` (${event.appointments.length} ${intl.formatMessage({
                  id: "appointments.many",
                })})`}
              </span>
            ) : (
              <span>
                {", "}
                <FormattedTime value={Date.parse(event.startDate)} timeZone={DEFAULT_TIMEZONE} />
                {" - "}
                <FormattedTime value={Date.parse(event.endDate)} timeZone={DEFAULT_TIMEZONE} />
              </span>
            )}
          </Paragraph>
        );
      }
      case EVENT_TYPES.flexible:
        return <Description>{event.description}</Description>;
      case EVENT_TYPES.continuous:
        if (event.appointments && event.appointments.length > 0) {
          return (
            <Paragraph mono>
              <Icon name="calendar" color="dark" spaced="right" size="small" />
              {event.appointments.map(({ startDate, endDate }, i) => (
                <span key={startDate}>
                  <FormattedDate weekday="short" value={Date.parse(startDate)} />
                  {". "}
                  <FormattedTime value={Date.parse(startDate)} timeZone={DEFAULT_TIMEZONE} />
                  -
                  <FormattedTime value={Date.parse(endDate)} timeZone={DEFAULT_TIMEZONE} />
                  {i < event.appointments.length - 1 && ", "}
                </span>
              ))}
              <EventAdditionalInfo>
                {event.endDate && (
                  <FormattedMessage
                    id="components.Course.period"
                    values={{
                      endDate: (
                        <FormattedDate
                          month="short"
                          day="numeric"
                          value={Date.parse(event.endDate)}
                        />
                      ),
                      startDate: (
                        <FormattedDate
                          month="short"
                          day="numeric"
                          value={Date.parse(event.startDate)}
                        />
                      ),
                    }}
                  />
                )}
              </EventAdditionalInfo>
            </Paragraph>
          );
        }
        return <Description>{event.description}</Description>;
      default:
        return "";
    }
  };
  return (
    <Wrapper>
      <HeaderWrapper>
        {image && <HeaderImage style={{ backgroundImage: `url('${image}')` }} />}
        <section>
          <TitleWrapper>
            <Heading>
              <FormattedMessage id="components.Booking.title" tagName="strong" />
            </Heading>
          </TitleWrapper>
          <TitleWrapper>
            <CourseTitle>{truncate(course.name, 50)}</CourseTitle>
            <Hidden size="sm md" display="inline-block">
              <Icon name="chevron-right" color="secondary" spaced="x" />
            </Hidden>
            <CourseTitle>
              <strong>{event.name}</strong>
              {event.isProtected && (
                <AuthStatus>
                  <Icon
                    name={unlocked ? "Unlock" : "Lock"}
                    color={unlocked ? "success" : "danger"}
                    title={<formatMessage id="components.Booking.password.title" />}
                  />
                </AuthStatus>
              )}
            </CourseTitle>
          </TitleWrapper>
          {priceOption && (
            <PriceOptionWrapper>
              <Icon name="tag" color="info" />
              <PriceOption selected>
                <span>{priceOption.name}</span>
                <span>{"-"}</span>
                <FormattedNumber value={priceOption.price / 100} {...currencyFormat(priceOption)} />
              </PriceOption>
            </PriceOptionWrapper>
          )}
        </section>
      </HeaderWrapper>

      {event && <AppointmentInfo>{getAppointmentsInfo()}</AppointmentInfo>}
    </Wrapper>
  );
};

Title.propTypes = {
  course: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  priceOption: PropTypes.object,
  unlocked: PropTypes.bool,
  intl: intlShape.isRequired,
  image: PropTypes.string,
};

Title.defaultProps = {
  unlocked: false,
  priceOption: null,
  image: null,
};

export default injectIntl(Title);
